/**
 * NO03 - Fakturakopi Search Component
 * Search form with advanced search options
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Paper,
    TextField,
    Button,
    Collapse,
    Typography,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    IconButton,
    Popover,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ExpandLess, InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb'; // Norwegian Bokmål locale
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import * as actionsNO03 from '../../../store/actions/indexNO03';
import { NO03SearchParams } from '../../../models/NO03';
import FirmNavnId from '../../../models/FirmNavnId';

const RED = '#ab273b';
const RED_DARK = '#8e1f30';
const neutralTheme = createTheme({ palette: { primary: { main: '#424242' } } });

const useStyles = makeStyles()((theme) => ({
    searchContainer: {
        padding: theme.spacing(3),
        margin: theme.spacing(2),
        backgroundColor: '#fff',
    },
    title: {
        marginBottom: theme.spacing(1),
        fontWeight: 500,
        fontSize: '1.5rem',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(3),
        fontSize: '0.875rem',
    },
    firmaSelectBox: {
        marginBottom: theme.spacing(2),
        maxWidth: '400px',
        '& .MuiOutlinedInput-root': {
            height: '40px',
        },
        '& .MuiInputBase-input': {
            padding: '8px 14px',
        },
    },
    quickSearchWrapper: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        maxWidth: '500px',
    },
    quickSearchField: {
        flex: 1,
        backgroundColor: '#f5f5f5',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
            height: '40px',
        },
        '& .MuiInputBase-input': {
            padding: '8px 14px',
        },
    },
    searchButton: {
        minWidth: '80px',
        height: '40px',
        textTransform: 'none',
        fontSize: '0.95rem',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    advancedToggle: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: '#000',
        fontSize: '0.875rem',
        marginBottom: theme.spacing(2),
        '&:hover': {
            color: '#000',
        },
    },
    advancedSection: {
        backgroundColor: '#f9f9f9',
        padding: theme.spacing(3),
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2),
    },
    dateField: {
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            height: '40px',
        },
        '& .MuiInputBase-input': {
            padding: '8px 14px',
        },
    },
}));

export default function NO03Search() {
    const { classes } = useStyles();
    const dispatch = useDispatch();

    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [infoAnchor, setInfoAnchor] = useState<HTMLButtonElement | null>(null);

    // Internal UI state - more flexible for user input
    const [searchState, setSearchState] = useState({
        companyId: '',
        quickSearch: '',
        quickSearchType: 'invoice' as 'invoice' | 'order',
        customerNumber: '',
        amount: '',
        fromDate: '',
        toDate: '',
    });

    const firmaList: FirmNavnId[] = useSelector((state: AppState) => state.NO03.firmaList);
    const loading = useSelector((state: AppState) => state.NO03.loading);
    const userJWT = useSelector((state: AppState) => state.auth.userJWT);
    const userDepartment = userJWT?.Department ?? '';

    useEffect(() => {
        if (firmaList.length > 0) {
            const firma = [...firmaList].sort((a, b) => a.firmID - b.firmID)[0];
            dispatch(actionsNO03.NO03SetFirma(firma));
            setSearchState((prev) => ({
                ...prev,
                companyId: firma.firmID.toString(),
            }));
        } else if (userDepartment) {
            setSearchState((prev) => ({
                ...prev,
                companyId: userDepartment.padStart(3, '0'),
            }));
        }
    }, [firmaList, userDepartment, dispatch]);

    const handleInputChange = (field: string, value: any) => {
        setSearchState({
            ...searchState,
            [field]: value,
        });
    };

    const handleFirmaChange = (firmaId: number) => {
        const firma = firmaList.find((f) => f.firmID === firmaId);
        if (firma) {
            dispatch(actionsNO03.NO03SetFirma(firma));
            // Convert firma ID to 3-character string
            const companyIdStr = firmaId.toString().padStart(3, '0');
            setSearchState({
                ...searchState,
                companyId: companyIdStr,
            });
        }
    };

    const handleSearch = () => {
        const hasFilter =
            searchState.quickSearch.trim() ||
            searchState.customerNumber.trim() ||
            searchState.amount.trim() ||
            searchState.fromDate ||
            searchState.toDate;
        if (!hasFilter) {
            setSearchError('Du må fylle inn minst ett søkefelt før du kan søke.');
            return;
        }
        setSearchError('');

        // Map UI state to backend contract
        const params: NO03SearchParams = {
            companyId: searchState.companyId.padStart(3, '0'),
        };

        // Map to invoice or order number based on selected type
        if (searchState.quickSearch) {
            if (searchState.quickSearchType === 'order') {
                params.orderNumber = searchState.quickSearch.trim();
            } else {
                params.invoiceNumber = searchState.quickSearch.trim();
            }
        }

        // Customer number - convert to number if valid
        if (searchState.customerNumber) {
            const custNum = parseInt(searchState.customerNumber, 10);
            if (!isNaN(custNum)) {
                params.customerNumber = custNum;
            }
        }

        // Amount - convert to number if valid
        if (searchState.amount) {
            const amountNum = parseFloat(searchState.amount);
            if (!isNaN(amountNum)) {
                params.amount = amountNum;
            }
        }

        // Date range
        if (searchState.fromDate) {
            params.fromDate = searchState.fromDate;
        }
        if (searchState.toDate) {
            params.toDate = searchState.toDate;
        }

        dispatch(actionsNO03.NO03SearchRequest(params));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClear = () => {
        setSearchState({
            companyId: searchState.companyId,
            quickSearch: '',
            quickSearchType: 'invoice',
            customerNumber: '',
            amount: '',
            fromDate: '',
            toDate: '',
        });
        setSearchError('');
        dispatch(actionsNO03.NO03ClearResults());
    };

    return (
        <ThemeProvider theme={neutralTheme}>
        <Paper className={classes.searchContainer} elevation={2}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography variant="h5" className={classes.title}>
                        Fakturakopi
                    </Typography>
                    <Typography variant="body2" className={classes.subtitle}>
                        Søk og skriv ut fakturakopier
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={(e) => setInfoAnchor(e.currentTarget)}
                    sx={{ color: '#2e7d32' }}
                >
                    <InfoOutlined />
                </IconButton>
            </Box>
            <Popover
                open={Boolean(infoAnchor)}
                anchorEl={infoAnchor}
                onClose={() => setInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Typography sx={{ p: 2, maxWidth: 340, fontSize: '0.875rem' }}>
                    Søk opp og vis fakturakopier. Du kan filtrere på kunde, fakturanummer, beløp og dato. Klikk på en faktura for å se detaljer, laste ned PDF eller sende kopi på e-post.
                </Typography>
            </Popover>

            {/* Firma Selection */}
            <Box className={classes.firmaSelectBox}>
                <FormControl fullWidth size="small">
                    <InputLabel>Velg firma</InputLabel>
                    <Select
                        value={parseInt(searchState.companyId, 10) || ''}
                        onChange={(e) => handleFirmaChange(Number(e.target.value))}
                        label="Velg firma"
                    >
                        <MenuItem value="">
                            <em>Velg firma</em>
                        </MenuItem>
                        {firmaList.length > 0
                            ? [...firmaList].sort((a, b) => a.firmID - b.firmID).map((firma) => (
                                <MenuItem key={firma.firmID} value={firma.firmID}>
                                    {firma.firmID} - {firma.firmName}
                                </MenuItem>
                            ))
                            : userDepartment && (
                                <MenuItem value={parseInt(userDepartment, 10)}>
                                    {userDepartment}{userJWT?.CompanyName ? ` - ${userJWT.CompanyName}` : ''}
                                </MenuItem>
                            )
                        }
                    </Select>
                </FormControl>
            </Box>

            {/* Quick Search with type toggle and button inline */}
            <Box className={classes.quickSearchWrapper}>
                <ToggleButtonGroup
                    value={searchState.quickSearchType}
                    exclusive
                    size="small"
                    onChange={(_e, val) => { if (val) handleInputChange('quickSearchType', val); }}
                    sx={{ height: '40px', flexShrink: 0 }}
                >
                    <ToggleButton
                        value="invoice"
                        sx={{
                            textTransform: 'none',
                            px: 2,
                            fontSize: '0.85rem',
                            '&.Mui-selected': {
                                backgroundColor: '#ab273b',
                                color: '#fff',
                                '&:hover': { backgroundColor: '#8e1f30' },
                            },
                        }}
                    >
                        Faktura
                    </ToggleButton>
                    <ToggleButton
                        value="order"
                        sx={{
                            textTransform: 'none',
                            px: 2,
                            fontSize: '0.85rem',
                            '&.Mui-selected': {
                                backgroundColor: '#ab273b',
                                color: '#fff',
                                '&:hover': { backgroundColor: '#8e1f30' },
                            },
                        }}
                    >
                        Ordre
                    </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={searchState.quickSearchType === 'order' ? 'Søk på ordrenummer' : 'Søk på fakturanummer'}
                    value={searchState.quickSearch}
                    onChange={(e) => handleInputChange('quickSearch', e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={classes.quickSearchField}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<SearchIcon fontSize="small" />}
                    onClick={handleSearch}
                    disabled={loading}
                    className={classes.searchButton}
                    sx={{ backgroundColor: RED, '&:hover': { backgroundColor: RED_DARK } }}
                >
                    Søk
                </Button>
            </Box>

            {searchError && (
                <Alert severity="error" sx={{ mb: 2, maxWidth: '560px' }} onClose={() => setSearchError('')}>
                    {searchError}
                </Alert>
            )}

            {/* Advanced Search Toggle */}
            <Box
                className={classes.advancedToggle}
                onClick={() => setAdvancedOpen(!advancedOpen)}
            >
                {advancedOpen ? <ExpandLess /> : <ExpandMore />}
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {advancedOpen ? 'Tilbake til hurtigsøk' : 'Avansert søk'}
                </Typography>
            </Box>

            {/* Advanced Search Section */}
            <Collapse in={advancedOpen}>
                <Box className={classes.advancedSection}>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} sx={{ maxWidth: 800 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Søk på kunde"
                                placeholder="F.eks. 500190"
                                value={searchState.customerNumber}
                                onChange={(e) => handleInputChange('customerNumber', e.target.value)}
                                onKeyDown={handleKeyDown}
                                className={classes.dateField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Søk på beløp"
                                placeholder="F.eks. 10999"
                                value={searchState.amount}
                                onChange={(e) => handleInputChange('amount', e.target.value)}
                                onKeyDown={handleKeyDown}
                                className={classes.dateField}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                        {/* Fra dato */}
                        <Box sx={{ gridColumn: '1 / 2' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker
                                    label="Fra dato"
                                    format="DD.MM.YYYY"
                                    value={searchState.fromDate ? dayjs(searchState.fromDate) : null}
                                    onChange={(date: Dayjs | null) => {
                                        handleInputChange('fromDate', date ? date.format('YYYY-MM-DD') : '');
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            placeholder: 'Velg dato',
                                            className: classes.dateField,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        {/* Til dato */}
                        <Box sx={{ gridColumn: '2 / 3' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker
                                    label="Til dato"
                                    format="DD.MM.YYYY"
                                    value={searchState.toDate ? dayjs(searchState.toDate) : null}
                                    onChange={(date: Dayjs | null) => {
                                        handleInputChange('toDate', date ? date.format('YYYY-MM-DD') : '');
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            placeholder: 'Velg dato',
                                            className: classes.dateField,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        {/* Action Buttons */}
                        <Box display="flex" gap={2} justifyContent="flex-end" sx={{ gridColumn: '1 / -1' }}>
                            <Button
                                variant="outlined"
                                onClick={handleClear}
                                sx={{ borderColor: RED, color: RED, '&:hover': { borderColor: RED_DARK, backgroundColor: 'rgba(171,39,59,0.04)' } }}
                            >
                                Nullstill
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                disabled={loading}
                                sx={{ backgroundColor: RED, '&:hover': { backgroundColor: RED_DARK } }}
                            >
                                Søk
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Collapse>
        </Paper>
        </ThemeProvider>
    );
}
